// One consumer complaint (migration 042): what the consumer said, where the case stands, and the
// actions each person may take.
//
//   Rachel (COMPLAINT_TRIAGE): take the case, acknowledge a call or letter by hand, move the stage,
//     add a note, close a Low, Medium or None case once QA has found on it.
//   QA (RELEASE): set severity and category, record the finding and action, close any case, reopen.
// The server checks the grant and the case's rules again whatever this screen shows. Every action is
// written back to the ActiveCampaign deal at once; one that did not land is listed as "not yet in
// ActiveCampaign" and the 10-minute mirror retries it. Consumer emails are still sent from the deal
// in ActiveCampaign (Amit, 31 Oct: AC stays the conversation until website v2).
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Fact, Facts } from '@/components/PageFacts';
import { Saved } from '@/components/Saved';
import { api } from '../../lib/api';
import { signInAgain } from '../../config/auth';
import { ComplaintPhotoGrid } from './ComplaintPhotos';
import { ComplaintBatch } from './ComplaintBatch';
import { ComplaintTrace } from './ComplaintTrace';
import { Compose, Conversation } from './ComplaintConversation';
import { CaseProgress } from './ComplaintProgress';
import { fieldClass, labelClass, officeFocus, serverMessage, useMyGrants, usePhone, useResource } from '../../shells/OfficeShell';
import { NcrPanel, useNcrConfig } from './RaiseNcr';
import { PutOnHold, useHoldConfig } from './BatchHolds';
import {
  CHANNEL_LABEL,
  EVENT_LABEL,
  SEVERITY_TONE,
  byWhom,
  clockPhrase,
  newKey,
  when,
  type ComplaintEvent,
  type ComplaintView,
  type ComplaintsConfig,
  type PilotPayload,
  type PilotSuggestion,
  CASE_TABS,
  caseTab,
  type CaseTab,
  type Severity,
} from './complaintsModel';

interface CasePayload {
  complaint: ComplaintView;
  events: ComplaintEvent[];
  sameBatch: Array<{ id: number; caseNo: string; receivedAt: string; message: string | null; severity: Severity | null; closed: boolean }>;
  /** The emails with the consumer and their photos: every complaint reader's (2 Nov 2026). Absent from an older API. */
  conversation?: Pick<PilotPayload, 'messages' | 'photos'>;
  /** Only for the week-2 pilot's people (QUALITY_COMPLAINT_PILOT). */
  pilot?: PilotPayload;
}

const card = 'flex flex-col gap-(--s3) rounded-md border border-(--border) bg-(--card) p-(--s4)';
const h2 = 'm-0 text-(length:--fs-sm) font-semibold';

function useAction(id: number, onDone: (said: string) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(body: Record<string, unknown>, said: string) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ ac?: { written: string[]; failed: string[] } }>(`/complaints/${id}/events`, { clientKey: newKey(), ...body });
      const failed = r.ac?.failed ?? [];
      onDone(failed.length ? `${said} Not yet in ActiveCampaign (${failed.join(', ')}): the next sync retries it.` : `${said}`);
      return true;
    } catch (err) {
      setError(serverMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
      {error}
    </p>
  ) : null;
}

type Done = (said: string) => void;

function SeverityForm({ c, config, hint, onDone }: { c: ComplaintView; config: ComplaintsConfig; hint: PilotSuggestion | null; onDone: Done }) {
  const act = useAction(c.id, onDone);
  const [severity, setSeverity] = useState<string>(c.severity?.value ?? '');
  const [category, setCategory] = useState<string>(c.category?.value ?? '');
  return (
    <form
      className="flex flex-col gap-(--s2)"
      onSubmit={(e) => {
        e.preventDefault();
        void act.run({ type: 'SEVERITY_SET', severity, category }, `Severity set: ${severity}, ${category}.`);
      }}
    >
      {hint && (hint.severity || hint.category) ? (
        <p className="m-0 text-(length:--fs-sm)">
          <span className="font-semibold">Claude suggests:</span> {[hint.severity, hint.category].filter(Boolean).join(', ')}
          {hint.reasoning ? <span className="text-(--muted)">. {hint.reasoning}</span> : null}
        </p>
      ) : null}
      {config.severityTable ? (
        <details className="text-(length:--fs-sm)">
          <summary className={`flex min-h-[44px] cursor-pointer items-center sm:min-h-0 ${officeFocus}`}>SOP Table 2: which severity</summary>
          {(['High', 'Medium', 'Low'] as const).map((sev) => (
            <div key={sev} className="mt-(--s2)">
              <p className="m-0 font-semibold">{sev}</p>
              <ul className="m-0 pl-(--s4)">
                {config.severityTable!.rows[sev].map((r) => (
                  <li key={`${sev}-${r.type}`}>
                    <span className="font-semibold">{r.type}:</span> {r.examples}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p className="m-0 mt-(--s2) text-(--muted)">{config.severityTable.note} None: not a product fault (a suggestion, compliment or enquiry).</p>
        </details>
      ) : null}
      <div className="grid grid-cols-1 gap-(--s2) sm:grid-cols-2">
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Severity</span>
          <select id="sev" className={`${fieldClass} ${officeFocus}`} value={severity} onChange={(e) => setSeverity(e.target.value)} required>
            <option value="">Choose</option>
            {config.severities.map((s) => (
              <option key={s} value={s}>
                {s}
                {s === 'None' ? ' (suggestion or compliment)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Category</span>
          <select id="cat" className={`${fieldClass} ${officeFocus}`} value={category} onChange={(e) => setCategory(e.target.value)} required>
            <option value="">Choose</option>
            {config.categories.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ErrorLine error={act.error} />
      <Button type="submit" disabled={act.busy} className="w-fit">
        {act.busy ? 'Saving…' : c.severity?.from === 'APP' ? 'Change severity' : 'Set severity'}
      </Button>
    </form>
  );
}

function TextForm({ caseId, id, label, placeholder, button, busyLabel, type, onDone, said, initial = '' }: { caseId: number; id: string; label: string; placeholder?: string; button: string; busyLabel: string; type: string; onDone: Done; said: string; initial?: string }) {
  const act = useAction(caseId, onDone);
  const [text, setText] = useState(initial);
  return (
    <form
      className="flex flex-col gap-(--s2)"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        if (await act.run({ type, text: text.trim() }, said)) setText('');
      }}
    >
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>{label}</span>
        <textarea id={id} name={id} rows={3} required className={`${fieldClass} ${officeFocus}`} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} />
      </label>
      <ErrorLine error={act.error} />
      <Button type="submit" disabled={act.busy || !text.trim()} variant="outline" className="w-fit">
        {act.busy ? busyLabel : button}
      </Button>
    </form>
  );
}

function StageForm({ c, config, onDone }: { c: ComplaintView; config: ComplaintsConfig; onDone: Done }) {
  const act = useAction(c.id, onDone);
  const [stage, setStage] = useState(c.stage === 'RESOLVED' ? '' : c.stage);
  const choices = config.stages.filter((s) => s.key !== 'RESOLVED');
  return (
    <form
      className="flex flex-wrap items-end gap-(--s2)"
      onSubmit={(e) => {
        e.preventDefault();
        void act.run({ type: 'STAGE_SET', stage }, `Stage: ${choices.find((s) => s.key === stage)?.label ?? stage}.`);
      }}
    >
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>Stage</span>
        <select id="stage" className={`${fieldClass} ${officeFocus}`} value={stage} onChange={(e) => setStage(e.target.value)}>
          {choices.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" variant="outline" disabled={act.busy || stage === c.stage}>
        {act.busy ? 'Moving…' : 'Move'}
      </Button>
      <div className="basis-full">
        <ErrorLine error={act.error} />
      </div>
    </form>
  );
}

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className={card}>
      <div className="flex flex-wrap items-baseline justify-between gap-(--s2)">
        <h2 className={h2}>{title}</h2>
        {aside ? <span className="text-[12px] text-(--muted)">{aside}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** Pilot: post this case's arrival card to your chat with the Quality bot. */
function CardButton({ id, cards }: { id: number; cards: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  if (cards !== 'on') return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={state === 'busy'}
        onClick={async () => {
          setState('busy');
          setError(null);
          try {
            await api.post(`/complaints/${id}/card`, {});
            setState('sent');
          } catch (err) {
            setError(serverMessage(err));
            setState('idle');
          }
        }}
      >
        {state === 'busy' ? 'Sending…' : state === 'sent' ? 'Card sent' : 'Send me the card'}
      </Button>
      {error ? (
        <span role="alert" className="text-(length:--fs-sm) text-(--warn)">
          {error}
        </span>
      ) : null}
    </>
  );
}

function eventLine(e: ComplaintEvent): string {
  const who = e.via === 'MIRROR' ? 'ActiveCampaign' : e.via === 'MAILBOX' ? 'feedback@' : byWhom('APP', e.byUpn);
  switch (e.eventType) {
    case 'SEVERITY_SET':
      return `${who}: ${e.severity}, ${e.category}`;
    case 'STAGE_SET':
      return `${who}: ${e.stage?.replace(/_/g, ' ').toLowerCase()}`;
    case 'AC_OBSERVED': {
      const p = e.payload ?? {};
      const bits = ['severity', 'category', 'stage'].filter((k) => p[k]).map((k) => `${k} ${String(p[k]).replace(/_/g, ' ').toLowerCase()}`);
      return `the deal read: ${bits.join(', ') || 'no QA fields yet'}`;
    }
    default:
      return e.text ? `${who}: ${e.text}` : who;
  }
}

/** What an NCR or hold from a case starts with: the case's batch, no item (a case names a product, not a D365 item). */
function casePrefill(c: ComplaintView) {
  const what = [c.product, c.batchCode ? `batch ${c.batchCode}` : null].filter(Boolean).join(', ');
  return {
    vendor: null,
    lines: c.batchCode ? [{ item: '', label: c.product ?? 'the product', batches: [c.batchCode] }] : [],
    description: `Consumer complaint ${c.caseNo}${what ? ` (${what})` : ''}: ${c.finding?.value ?? (c.message ?? '').slice(0, 400)}`,
  };
}

export function ComplaintCase() {
  const { complaintId } = useParams();
  return <CasePage key={complaintId} complaintId={complaintId} />;
}

function CasePage({ complaintId }: { complaintId: string | undefined }) {
  const id = Number(complaintId);
  const data = useResource<CasePayload>(Number.isInteger(id) && id > 0 ? `/complaints/${id}` : null);
  const config = useResource<ComplaintsConfig>('/complaints/config');
  const grants = useMyGrants();
  // Phone (below sm): two tabs, Act and Details, kept in the URL (UI/UX pass, 3 Nov 2026: the page ran to
  // 5,000 px with Clocks, Stage, Timeline and Add a note under everything). Desktop keeps its two columns.
  const phone = usePhone();
  const DetailsPanel = phone ? 'div' : 'aside';
  const [params, setParams] = useSearchParams();
  const tab = caseTab(params.get('tab'));
  const selectTab = (t: CaseTab) => {
    const next = new URLSearchParams(params);
    if (t === 'act') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };
  const ncrConfig = useNcrConfig();
  const holdConfig = useHoldConfig();
  const [saved, setSaved] = useState<string | null>(null);
  const done = (said: string) => {
    setSaved(said);
    data.reload();
  };
  // Only "Take the case" acts from the header; each form below has its own action and error.
  const act = useAction(id, done);
  const c = data.data?.complaint;
  const cfg = config.data;
  const mine = new Set(cfg?.myGrants ?? []);
  const isQa = mine.has('RELEASE');
  const isTriage = mine.has('COMPLAINT_TRIAGE');

  if (data.error || config.error) {
    return (
      <div role="alert" className="flex flex-col gap-(--s2)">
        <Breadcrumb items={[{ label: 'Complaints', to: '/office/complaints' }, { label: `Case ${complaintId}` }]} />
        <p className="m-0 font-semibold text-(--warn)">Could not read this case: {data.error ?? config.error}</p>
        <div className="flex flex-wrap gap-(--s2)">
          {/^Your sign-in/.test(data.error ?? config.error ?? '') ? (
            <Button type="button" onClick={signInAgain}>
              Sign in again
            </Button>
          ) : null}
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              data.reload();
              config.reload();
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!c || !cfg) return <p role="status" className="m-0 text-(--muted)">Reading the case…</p>;

  const canClose = !c.closed && (isQa || (isTriage && c.severity?.value !== 'High'));
  const closeBlocked = !c.severity ? 'QA sets the severity first.' : !c.finding ? 'QA records the finding first.' : null;
  const pilot = data.data!.pilot;
  const conversation = data.data!.conversation ?? pilot ?? null;
  const ackOwed = !c.closed && !c.clocks.ackMet;
  const conversationBlock = (
    <>
      {conversation ? <Conversation messages={conversation.messages} /> : null}
      {pilot?.replies && !c.closed ? (
        <Compose key={`${c.id}-${pilot.messages.length}`} caseId={c.id} pilot={pilot} templates={cfg.templates ?? []} isQa={isQa} canSend={isQa || isTriage} onSent={done} />
      ) : null}
    </>
  );
  const facts: Array<[string, string | null]> = [
    ['Consumer', c.consumerName],
    ['Country', c.country],
    ['Bought at', c.placeOfPurchase],
    ['Size', c.productSize],
    ['Best before', c.bestBefore],
    ['Voucher', c.voucher ? `$${(c.voucher / 100).toFixed(2)}` : null],
  ];

  return (
    <div className="flex flex-col gap-(--s4)">
      <Breadcrumb items={[{ label: 'Complaints', to: '/office/complaints' }, { label: c.caseNo }]} />
      <header className="flex flex-wrap items-start justify-between gap-(--s3)">
        <div className="min-w-0">
          <h1 className="m-0 break-words font-display text-[30px] font-semibold leading-tight tracking-[-0.01em]">
            {c.caseNo} · {c.product ?? 'product not given'}
            {c.batchCode ? `, batch ${c.batchCode}` : ''}
          </h1>
          <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
            {c.severity ? (
              <>
                <span className={`mr-(--s2) rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${SEVERITY_TONE[c.severity.value]}`}>{c.severity.value}</span>
                <span className="sr-only"> </span>
              </>
            ) : null}
            {c.category ? `${c.category.value} · ` : ''}
            {c.owner ? `${byWhom('APP', c.owner.upn)} has it` : 'Nobody has it yet'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-(--s2)">
          {!c.closed && !c.owner && (isTriage || isQa) ? (
            <Button type="button" disabled={act.busy} onClick={() => void act.run({ type: 'TAKEN' }, 'You took the case.')}>
              {act.busy ? 'Taking…' : 'Take the case'}
            </Button>
          ) : null}
          {c.acDealUrl ? (
            <Button asChild variant="outline">
              <a href={c.acDealUrl} target="_blank" rel="noreferrer">
                <span className="sm:hidden">
                  Open the deal<span className="sr-only"> in ActiveCampaign</span>
                </span>
                <span className="hidden sm:inline">Open in ActiveCampaign</span>
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </Button>
          ) : null}
        </div>
      </header>

      <CaseProgress c={c} />

      {saved ? <Saved>{saved}</Saved> : null}
      {c.notInAc.length ? (
        <p className="m-0 text-(length:--fs-sm) font-semibold text-(--action-fg)">
          Not yet in ActiveCampaign: {c.notInAc.join(', ')}. The next sync writes it to the deal.
        </p>
      ) : null}
      <ErrorLine error={act.error} />

      {phone ? (
        <div role="tablist" aria-label="This case" className="flex gap-(--s1) border-b border-(--border)">
          {CASE_TABS.map((t) => {
            const selected = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`case-tab-${t.id}`}
                aria-selected={selected}
                aria-controls={`case-panel-${t.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => selectTab(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    selectTab(t.id === 'act' ? 'details' : 'act');
                    (document.getElementById(`case-tab-${t.id === 'act' ? 'details' : 'act'}`) as HTMLButtonElement | null)?.focus();
                  }
                }}
                className={`-mb-px inline-flex min-h-[44px] flex-1 items-center justify-center border-b-2 px-(--s3) text-(length:--fs-sm) font-semibold ${officeFocus} ${
                  selected ? 'border-(--accent-line) text-(--fg)' : 'border-transparent text-(--muted)'
                }`}
              >
                {t.label}
                {t.id === 'details' && !c.clocks.ackMet && !c.closed ? <span className="sr-only"> (acknowledgement owed)</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-(--s4)">
        <div
          id="case-panel-act"
          role={phone ? 'tabpanel' : undefined}
          aria-labelledby={phone ? 'case-tab-act' : undefined}
          hidden={phone && tab !== 'act'}
          className="flex min-w-0 flex-[999_1_560px] flex-col gap-(--s4)"
        >
          <Section title="The complaint" aside={`${CHANNEL_LABEL[c.channel] ?? c.channel} · ${when(c.receivedAt)}`}>
            {facts.some(([, v]) => v) ? (
              <Facts className="mt-0">
                {facts
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <Fact key={k} label={k}>
                      {v}
                    </Fact>
                  ))}
              </Facts>
            ) : null}
            <blockquote className="m-0 whitespace-pre-line break-words border-l-[3px] border-(--border-strong) bg-(--subtle) px-(--s3) py-(--s2) text-(length:--fs-sm) leading-relaxed">{c.message ?? 'No message.'}</blockquote>
            {conversation?.photos.length ? <ComplaintPhotoGrid caseId={c.id} caseNo={c.caseNo} photos={conversation.photos} /> : null}
            {(() => {
              const kept = new Set((conversation?.photos ?? []).map((p) => p.sourceUrl));
              const left = c.photos.filter((p) => !kept.has(p));
              return left.length ? (
                <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0 text-(length:--fs-sm)">
                  {left.map((p, i) => (
                    <li key={p}>
                      <a href={p} target="_blank" rel="noreferrer" className={`underline ${officeFocus}`}>
                        {kept.size ? `Photo ${kept.size + i + 1}` : `Photo ${i + 1}`} (on the form's host)<span className="sr-only"> opens in a new tab</span>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null;
            })()}
          </Section>

          {/* Traceability phase 1 (rule 36): the batch, resolved when the case arrived, beside the complaint. */}
          <ComplaintBatch caseId={c.id} canName={isQa || isTriage} closed={!!c.closed} initial={c.batch ?? null} />
          {c.batch?.batch ? <ComplaintTrace caseId={c.id} /> : null}

          {/* Order follows the work (ui-check rule 2): while the acknowledgement is owed, the reply comes before
              QA's severity; once acknowledged, the severity (which gates the rest) comes first. */}
          {ackOwed ? conversationBlock : null}

          <Section title="Severity and category" aside="QA sets these">
            {isQa && !c.closed ? (
              <SeverityForm c={c} config={cfg} hint={pilot?.suggestion ?? null} onDone={done} />
            ) : (
              <p className="m-0 text-(length:--fs-sm)">
                {c.severity ? `${c.severity.value}, ${c.category?.value ?? 'no category'}: set ${byWhom(c.severity.from, c.severity.byUpn)}, ${when(c.severity.at)}.` : 'Not set yet.'}
              </p>
            )}
            {data.data!.sameBatch.length ? (
              <div className="flex flex-col gap-(--s1)">
                <span className={labelClass}>Same product and batch</span>
                <ul className="m-0 flex list-none flex-col gap-(--s1) p-0 text-(length:--fs-sm)">
                  {data.data!.sameBatch.map((s) => (
                    <li key={s.id}>
                      <Link to={`/office/complaints/${s.id}`} className={`font-semibold underline ${officeFocus}`}>
                        {s.caseNo}
                      </Link>{' '}
                      {when(s.receivedAt)} · {s.severity ?? 'no severity'}
                      {s.closed ? ' · closed' : ''} · <span className="text-(--muted)">{(s.message ?? '').slice(0, 120)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          {ackOwed ? null : conversationBlock}

          <Section title="Finding and action" aside="QA records this">
            {c.finding ? (
              <p className="m-0 whitespace-pre-line break-words text-(length:--fs-sm)">
                {c.finding.value}
                <span className="block text-[12px] text-(--muted)">
                  {byWhom(c.finding.from, c.finding.byUpn)}, {when(c.finding.at)}
                </span>
              </p>
            ) : (
              <p className="m-0 text-(length:--fs-sm) text-(--muted)">No finding yet.</p>
            )}
            {isQa && !c.closed ? (
              <TextForm
                id="finding"
                label={c.finding ? 'Replace the finding' : 'The finding and the action'}
                placeholder="What QA checked and what was done…"
                button="Record finding"
                busyLabel="Recording…"
                type="FINDING_RECORDED"
                caseId={c.id}
                onDone={done}
                said="Finding recorded."
              />
            ) : null}
          </Section>

          {pilot && isQa ? (
            <Section title="NCR and QC Hold" aside="in D365, as you">
              {ncrConfig && !ncrConfig.enabled && holdConfig && !holdConfig.enabled ? (
                // Both switched off: one line and the door to D365 (ui-check rule 4), not two "off" notes.
                <p className="m-0 text-(length:--fs-sm)">
                  Raised and held in D365 for now.{' '}
                  {holdConfig.d365Link ? (
                    <a href={holdConfig.d365Link.replace('mi=InventBatch', 'mi=InventNonConformanceTable')} target="_blank" rel="noreferrer" className={`inline-flex min-h-[44px] items-center font-semibold underline sm:min-h-0 ${officeFocus}`}>
                      Open Non-conformances in D365<span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  ) : null}
                </p>
              ) : (
                <>
                  {ncrConfig && !ncrConfig.enabled ? <p className="m-0 text-(length:--fs-sm) text-(--muted)">Raising an NCR here is off: raise it in D365 and give its number to the hold.</p> : null}
                  <NcrPanel config={ncrConfig} source={{ kind: 'COMPLAINT', id: c.id }} prefill={casePrefill(c)} grants={grants} />
                  {!c.closed ? <PutOnHold config={holdConfig} source={{ kind: 'COMPLAINT', id: c.id }} prefill={casePrefill(c)} grants={grants} /> : null}
                  {holdConfig && !holdConfig.enabled ? <p className="m-0 text-(length:--fs-sm) text-(--muted)">QC Holds from the app are off.</p> : null}
                </>
              )}
            </Section>
          ) : null}

          {!c.closed && (isTriage || isQa) ? (
            <Section title="Close the case">
              {closeBlocked ? (
                <p className="m-0 text-(length:--fs-sm) text-(--muted)">{closeBlocked}</p>
              ) : canClose ? (
                <TextForm id="close" label="How it was resolved" button="Close the case" busyLabel="Closing…" type="CLOSED" caseId={c.id} onDone={done} said="Case closed. The deal moves to Resolved." />
              ) : (
                <p className="m-0 text-(length:--fs-sm) text-(--muted)">A High case is closed by QA.</p>
              )}
            </Section>
          ) : null}
          {c.closed ? (
            <Section title="Closed" aside={`${byWhom(c.closed.from, c.closed.byUpn)}, ${when(c.closed.at)}`}>
              <p className="m-0 text-(length:--fs-sm)">{c.closed.note ?? 'Resolved in ActiveCampaign.'}</p>
              {isQa ? <TextForm caseId={c.id} id="reopen" label="Why it is reopened" button="Reopen" busyLabel="Reopening…" type="REOPENED" onDone={done} said="Case reopened." /> : null}
            </Section>
          ) : null}
        </div>

        {/* A tab panel, not an aside, on the phone: <aside role="tabpanel"> is not an allowed pairing (axe). */}
        <DetailsPanel
          id="case-panel-details"
          role={phone ? 'tabpanel' : undefined}
          aria-labelledby={phone ? 'case-tab-details' : undefined}
          hidden={phone && tab !== 'details'}
          className="flex min-w-0 flex-[1_1_300px] flex-col gap-(--s4)"
        >
          <Section title="Clocks">
            <dl className="m-0 flex flex-col gap-(--s2) text-(length:--fs-sm)">
              <div className="flex justify-between gap-(--s2)">
                <dt>Acknowledge, 1 day</dt>
                <dd className="m-0 font-semibold tabular-nums">
                  {c.clocks.ackMet ? `met ${when(c.acknowledgedAt)}` : clockPhrase(c).text}
                </dd>
              </div>
              <div className="flex justify-between gap-(--s2)">
                <dt>Close, {c.clocks.closeDays} days</dt>
                <dd className="m-0 font-semibold tabular-nums">
                  {c.closed ? (c.clocks.closeOverDays > 0 ? `${c.clocks.closeOverDays} d late` : 'in time') : c.clocks.closeOverDays > 0 ? `${c.clocks.closeOverDays} d over` : `${-c.clocks.closeOverDays} d left`}
                </dd>
              </div>
            </dl>
            {!c.closed && !c.clocks.ackMet && (isTriage || isQa) ? (
              <TextForm caseId={c.id} id="ack" label="Acknowledged by phone or letter" button="Record acknowledgement" busyLabel="Recording…" type="ACKNOWLEDGED" onDone={done} said="Acknowledgement recorded." />
            ) : null}
          </Section>

          {!c.closed && (isTriage || isQa) ? (
            <Section title="Stage">
              <StageForm c={c} config={cfg} onDone={done} />
            </Section>
          ) : null}

          <Section title="Timeline" aside={pilot ? <CardButton id={c.id} cards={pilot.cards} /> : undefined}>
            <ol className="m-0 flex list-none flex-col gap-(--s2) p-0 text-[12px] leading-snug break-words">
              <li>
                <span className="text-(--muted) tabular-nums">{when(c.receivedAt)}</span>
                <br />
                Received ({CHANNEL_LABEL[c.channel] ?? c.channel}){c.acDealId ? `, AC deal ${c.acDealId}` : `, recorded by ${byWhom('APP', c.recordedByUpn)}`}
              </li>
              {data.data!.events.map((e) => (
                <li key={e.id}>
                  <span className="text-(--muted) tabular-nums">{when(e.at)}</span>
                  <br />
                  <strong>{EVENT_LABEL[e.eventType] ?? e.eventType}</strong> · {eventLine(e)}
                </li>
              ))}
            </ol>
          </Section>

          {isTriage || isQa ? (
            <Section title="Add a note">
              <TextForm caseId={c.id} id="note" label="Note" button="Add note" busyLabel="Adding…" type="NOTE" onDone={done} said="Note added." />
            </Section>
          ) : null}
        </DetailsPanel>
      </div>
    </div>
  );
}
